import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '@/components/auth-context/auth-context';
import notifyHB from '@/config/notifyHB';
import { OnboardingStatus } from '@/entities/onboarding-status';
import { getOnboardingStatus } from '@/use-cases/get-onboarding-status';

// App.tsx renders the dashboard through an inline component, so it remounts
// on every app re-render; reuse a read from the same burst instead of
// refetching each time. Short, so coming back from the wizard reads fresh.
const CACHE_TTL_MS = 5_000;
let cached: {
	ulid: string;
	at: number;
	promise: Promise<OnboardingStatus | null>;
} | null = null;

function loadStatus(ulid: string) {
	if (
		cached &&
		cached.ulid === ulid &&
		Date.now() - cached.at < CACHE_TTL_MS
	) {
		return cached.promise;
	}
	const promise = getOnboardingStatus(ulid).catch((error) => {
		notifyHB('dashboard', 'useActiveOnboarding', error);
		cached = null;
		return null;
	});
	cached = { ulid, at: Date.now(), promise };
	return promise;
}

/** Test hook: forget the cached status. */
export function resetActiveOnboardingCache() {
	cached = null;
}

/**
 * Status of the user's onboarding process in flight, if any. GET /user's
 * active process wins; the ulid login stored is the fallback.
 */
export function useActiveOnboarding() {
	const { userProfile } = useAuthContext();
	const [status, setStatus] = useState<OnboardingStatus | null>(null);

	const ulid =
		userProfile?.ActiveProcessType === 'onboard' &&
		userProfile.ActiveProcessUlid
			? userProfile.ActiveProcessUlid
			: localStorage.getItem('onboarding-ulid');

	useEffect(() => {
		if (!ulid) {
			setStatus(null);
			return;
		}
		let cancelled = false;
		loadStatus(ulid).then((loaded) => {
			if (!cancelled) setStatus(loaded);
		});
		return () => {
			cancelled = true;
		};
	}, [ulid]);

	/** Re-reads the status, bypassing the shared read. */
	const refresh = useCallback(async () => {
		cached = null;
		const refreshed = ulid ? await loadStatus(ulid) : null;
		setStatus(refreshed);
		return refreshed;
	}, [ulid]);

	return { status, refresh };
}
